import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { cloneDeep, isEmpty } from 'lodash';
import JBImage from 'components/essentials/JBImage';
import Placeholder from 'components/consumer/Placeholder';
import RatingAndReviews from 'components/consumer/RatingAndReviews';
import MaterialThumbnail from 'components/consumer/MaterialThumbnail';
import isClient from 'commons/isClient';
import getShortCustomerName from 'commons/getShortCustomerName';
import FlatButtonFilters from 'components/consumer/FlatButtonFilters';
import FabricAdditionalAttributes from 'components/consumer/FabricAdditionalAttributes';
import Link from 'components/consumer/Link';
import IconVelvet from 'components/icons/iconVelvet';
import BrandTooltip from 'components/consumer/BrandTooltip';
import InfoTooltip from 'components/consumer/InfoTooltip';
import CtaButton from 'components/essentials/Cta/CtaButton';
import useScreen from 'hooks/useScreen';
import ReviewStars from 'components/consumer/ReviewStars';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import IconVerified from '../../../../icons/iconVerified';
import { transformInspirationImageUrl } from '../../../inspiration-photos-grid/search-state/utils';
import PDPReviewsMaterialFlyout from './PDPReviewsMaterialFlyout';
import PDPActiveFabric from '../../PDPActiveFabric';
import PDPFabricDetailSidebar from '../../PDPFabricDetailSidebar';
const filterTabs = [{
  label: 'Everything',
  id: 'everything'
}, {
  label: 'Reviews',
  id: 'review'
}, {
  label: 'Photos',
  id: 'photo'
}];
const scrollToReview = (type, index) => {
  if (index !== undefined) {
    let idx;
    if (type === 'photo' || type === 'review') {
      idx = index;
    }
    const targetElement = document.querySelectorAll('[data-xc="PDPCustomerPhotosReviewsModal__card"]')[idx];
    setTimeout(() => {
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 500);
  }
};
const PDPCustomerPhotosReviewsModal = ({
  name,
  loading,
  customerPhotos,
  customerReviews,
  averageRating,
  totalReviews,
  initialImageIndex,
  initialReviewIndex,
  upholsteryOptionId,
  selectedMaterial
}) => {
  const containerRefs = useRef({});
  const {
    isMobile
  } = useScreen();
  // state
  const [selectedFilter, setSelectedFilter] = useState('everything');
  const [hoveredMaterial, setHoveredMaterial] = useState(null);
  const [isMaterialHovered, setIsMaterialHovered] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [galleryType, setGalleryType] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [noOfReviewsToShow, setNoOFReviewToShow] = useState(50);
  const history = useHistory();

  // effects
  useEffect(() => {
    if (!loading) {
      if (initialImageIndex > -1) {
        scrollToReview('photo', initialImageIndex);
      }
      if (initialReviewIndex > -1) {
        scrollToReview('review', initialReviewIndex);
      }
    }
  }, [initialImageIndex, initialReviewIndex, loading]);
  useEffect(() => {
    const {
      tab
    } = isClient() ? queryString.parse(window.location.search) : {};
    const selectedTabObj = filterTabs.find(tabObj => tabObj.id === tab);
    if (!!selectedTabObj && Object.keys(selectedTabObj).length === 2) {
      setSelectedFilter(selectedTabObj.id);
    }
  }, []);
  const customToggleFlyout = useCallback((flyoutStatus, itemVal) => {
    if (!flyoutStatus) {
      setIsMaterialHovered(false);
      return;
    }
    const materialId = itemVal.itemMaterial.id;
    const newTop = containerRefs.current?.[`${itemVal.id}-${materialId}`]?.getBoundingClientRect().top || 0;
    const newLeftCalc = containerRefs.current?.[`${itemVal.id}-${materialId}`]?.getBoundingClientRect().x;
    const newLeft = newLeftCalc - 450 || 0;
    const newHoveredMaterial = {
      materialId,
      top: newTop,
      left: newLeft,
      subSubTitle: itemVal.itemMaterial.tier && itemVal.itemMaterial.group ? <FabricAdditionalAttributes selectedItemGroupMaterial={itemVal.itemMaterial.group.material} selectedItemGroupCleaningCode={itemVal.itemMaterial.group.cleaning_code} isPetAndKidFriendly={!!itemVal.itemMaterial.pet_friendly} isSustainable={!!itemVal.itemMaterial.sustainable} isGreenguardGold={!!itemVal.greenguard_fabric} selectedItemTier={itemVal.itemMaterial.tier} showCleaningToolTip={false} /> : null,
      subTitle: itemVal.itemMaterial.group?.material === 'velvet' ? <div className="items-center flex">
            <IconVelvet />
            <span className="text-[.8125rem] leading-[1.4375rem] font-bold text-[#af87a1] tracking-[.013rem]">
              Velvet
            </span>
          </div> : null,
      imageUrl: itemVal.itemMaterial?.image,
      content: itemVal.itemMaterial.description,
      title: itemVal.itemMaterial?.value.trim()
    };
    setHoveredMaterial(newHoveredMaterial);
    setIsMaterialHovered(true);
  }, []);
  const getImageObj = useCallback(img => ({
    id: img.id,
    type: 'photo',
    comment: img.comment,
    location: img.location,
    full_name: img.full_name,
    src: transformInspirationImageUrl(img.url),
    itemMaterial: img.itemMaterial,
    materialLink: img.materialLink,
    itemMaterialInactive: img.itemMaterialInactive
  }), []);
  const getReviewObj = useCallback(review => ({
    id: review.id,
    type: 'review',
    date: review.date,
    rate: review.rate,
    title: review.title,
    comment: review.comment,
    location: review.location,
    full_name: review.full_name,
    itemMaterial: review.itemMaterial,
    materialLink: review.materialLink,
    itemMaterialInactive: review.itemMaterialInactive
  }), []);
  const list = useMemo(() => {
    const updatedList = [];
    const total = (customerPhotos?.length || 0) + (customerReviews?.length || 0);
    for (let i = 0, j = 0, k = 0; i < total; i++) {
      [1, 2, 3].forEach(() => {
        if (customerPhotos[j]) updatedList.push(getImageObj(customerPhotos[j++]));
      });
      [1, 2].forEach(() => {
        if (customerReviews[k]) updatedList.push(getReviewObj(customerReviews[k++]));
      });
    }
    return updatedList;
  }, [customerPhotos, customerReviews, getImageObj, getReviewObj]);
  const allReviews = useMemo(() => {
    if (list.length > 0) {
      const filteredSelectedMaterialReview = list.filter(fr => fr?.itemMaterial?.id === selectedMaterial.materialId);
      const reviewWithoutSelectedMaterial = list.filter(fr_0 => fr_0?.itemMaterial?.id !== selectedMaterial.materialId);
      let reviews = [...filteredSelectedMaterialReview, ...reviewWithoutSelectedMaterial];
      if (selectedFilter !== 'everything') {
        reviews = reviews.filter(review_0 => review_0.type === selectedFilter);
      }
      return reviews;
    }
    return [];
  }, [list, selectedFilter, selectedMaterial.materialId]);
  const filteredReviews = useMemo(() => {
    if (allReviews.length) {
      const reviews_0 = cloneDeep(allReviews);
      return reviews_0.splice(0, noOfReviewsToShow);
    }
    return [];
  }, [allReviews, noOfReviewsToShow]);
  const onClose = useCallback(() => {
    setGalleryType('');
    setSidebarOpen(false);
  }, []);
  return <>
      {isMaterialHovered && <PDPReviewsMaterialFlyout top={hoveredMaterial.top} title={hoveredMaterial.title} content={hoveredMaterial.content} subTitle={hoveredMaterial.subTitle} imageUrl={hoveredMaterial.imageUrl} titleInfo={hoveredMaterial.titleInfo} subSubTitle={hoveredMaterial.subSubTitle} left={hoveredMaterial.left} />}
      <PDPFabricDetailSidebar isOpen={isSidebarOpen} onClose={onClose} fabricId={selectedMaterialId} mode="pdp" galleryType={galleryType} mobileHideHeader />
      <div className="PDPCustomerPhotosReviewsModal">
        <div className="max-w-[960px] mt-0 mx-auto mb-32">
          {loading ? <>
              <div className="mt-6 px-4 py-0 md:p-0 md:text-center [&_span]:mr-2 [&_span_svg]:w-3 [&_span_svg]:h-3 [&_span_svg]:fill-yellow [&_span_svg]:mt-1">
                <div className="max-[767px]:text-2xl max-[767px]:font-bold max-[767px]:mb-[.325rem] md:text-3xl md:font-bold md:pt-8 md:px-0 md:pb-2">
                  <Placeholder type="rect" loading style={{
                height: 23,
                width: !isMobile ? 474 : '100%',
                marginRight: 'auto',
                marginLeft: 'auto'
              }} />
                </div>

                <Placeholder type="rect" loading style={{
              height: 23,
              width: 179,
              marginRight: 'auto',
              marginLeft: 'auto'
            }} />
              </div>

              <div className="mt-5 mx-0 mb-0 px-4 py-0 md:pt-0 md:px-0 md:pb-14 md:mt-6 md:mx-0 md:mb-0 md:[border-bottom:1px_solid_#f1f1f1] [&>div]:md:justify-center">
                <Placeholder type="rect" loading style={{
              height: 35,
              width: 218,
              marginRight: 'auto',
              marginLeft: 'auto'
            }} />
              </div>

              <div className="PDPCustomerPhotosReviewsModal__list">
                <div className="flex flex-row px-0 py-[3.25rem] [border-top:1px_solid_#f1f1f1] first:[border-top:0] max-[767px]:flex-col first:max-[767px]:pt-[3.25rem]">
                  <div className="text-base font-bold px-5 py-0 md:w-[22%] md:pl-0 md:pr-[1%]">
                    <div className="max-[767px]:flex">
                      <div className="w-11 h-11 max-h-[3.75rem] mb-4 text-2xl text-gray-light6 bg-gray-light8 rounded-full mr-4 flex justify-center items-center">
                        <Placeholder type="round" loading style={{
                      height: 44,
                      width: 44
                    }} />
                      </div>

                      <div className="PDPCustomerPhotosReviewsModal__user-details">
                        <div className="[&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c]">
                          <Placeholder type="rect" loading style={{
                        height: 14,
                        width: 75
                      }} />
                        </div>
                        <div className="text-xs">
                          <Placeholder type="rect" loading style={{
                        height: 14,
                        width: 75,
                        marginTop: 5
                      }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-[77%] max-[767px]:w-full max-[767px]:px-5 max-[767px]:py-0 max-[767px]:mt-4">
                    <Placeholder type="rect" loading style={{
                  height: 14
                }} />

                    <Placeholder type="rect" loading style={{
                  height: 14,
                  marginTop: 11
                }} />

                    <Placeholder type="rect" loading style={{
                  height: 14,
                  marginTop: 11,
                  width: !isMobile ? 455 : '100%'
                }} />

                    <Placeholder type="rect" loading style={{
                  height: 566,
                  marginTop: 36
                }} />
                  </div>
                </div>

                <div className="flex flex-row px-0 py-[3.25rem] [border-top:1px_solid_#f1f1f1] first:[border-top:0] max-[767px]:flex-col first:max-[767px]:pt-[3.25rem]">
                  <div className="text-base font-bold px-5 py-0 md:w-[22%] md:pl-0 md:pr-[1%]">
                    <div className="max-[767px]:flex">
                      <div className="w-11 h-11 max-h-[3.75rem] mb-4 text-2xl text-gray-light6 bg-gray-light8 rounded-full mr-4 flex justify-center items-center">
                        <Placeholder type="round" loading style={{
                      height: 44,
                      width: 44
                    }} />
                      </div>

                      <div className="PDPCustomerPhotosReviewsModal__user-details">
                        <div className="[&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c]">
                          <Placeholder type="rect" loading style={{
                        height: 14,
                        width: 75
                      }} />
                        </div>
                        <div className="text-xs">
                          <Placeholder type="rect" loading style={{
                        height: 14,
                        width: 75,
                        marginTop: 5
                      }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-[77%] max-[767px]:w-full max-[767px]:px-5 max-[767px]:py-0 max-[767px]:mt-4">
                    <Placeholder type="rect" loading style={{
                  height: 14
                }} />

                    <Placeholder type="rect" loading style={{
                  height: 14,
                  marginTop: 11
                }} />

                    <Placeholder type="rect" loading style={{
                  height: 14,
                  marginTop: 11,
                  width: !isMobile ? 455 : '100%'
                }} />
                  </div>
                </div>
              </div>
            </> : <>
              <div className="mt-6 px-4 py-0 md:p-0 md:text-center [&_span]:mr-2 [&_span_svg]:w-3 [&_span_svg]:h-3 [&_span_svg]:fill-yellow [&_span_svg]:mt-1">
                <div className="max-[767px]:text-2xl max-[767px]:font-bold max-[767px]:mb-[.325rem] md:text-3xl md:font-bold md:pt-8 md:px-0 md:pb-2">
                  Customers love the {name}
                </div>

                <div className="md:flex md:justify-center">
                  <RatingAndReviews numberOfReviews={totalReviews} rating={averageRating} size="sm" />
                </div>
              </div>

              <div className="mt-5 mx-0 mb-0 px-4 py-0 md:pt-0 md:px-0 md:pb-14 md:mt-6 md:mx-0 md:mb-0 md:[border-bottom:1px_solid_#f1f1f1] [&>div]:md:justify-center">
                <FlatButtonFilters items={filterTabs} selectedItem={selectedFilter} setSelectedItem={id => {
              history.replace(`?tab=${id}`); // Clicking BACK on modal should go to PDP
              setSelectedFilter(id);
              setNoOFReviewToShow(50);
            }} />
              </div>

              <div className="PDPCustomerPhotosReviewsModal__list">
                {filteredReviews.map(item => {
              const hideMainSection = isMobile && item.type === 'photo' && !item.comment;
              const image = item.src ? <div className="mb-4">
                      <JBImage src={item.src} forPage="pdp" sizes="100vw" trim="auto" />
                    </div> : null;
              return <div key={`PDPCustomerPhotosReviewsModal__card__${item.id}`} data-xc="PDPCustomerPhotosReviewsModal__card" className={cx('flex flex-row-reverse px-0 py-[3.25rem] [border-top:1px_solid_#f1f1f1] first:[border-top:0] max-[767px]:flex-col first:max-[767px]:pt-[3.25rem]', {
                'max-[767px]:pt-0 max-[767px]:px-0 max-[767px]:pb-[3.25rem]': item.type === 'photo'
              })}>
                      {isMobile && image}
                      {!hideMainSection && <div className="w-[77%] max-[767px]:w-full max-[767px]:px-5 max-[767px]:py-0 max-[767px]:mt-4">
                          {item.title && <SanitizedHtml className="text-xl font-bold mb-2.5" html={item.title} />}
                          {item.rate && <div className="flex items-center justify-between mb-2.5">
                              <ReviewStars rating={item.rate} readOnly size="sm" />
                              {item.date && <div className="text-xs">
                                  {format(new Date(item.date), 'MMM dd, yyyy')}
                                </div>}
                            </div>}

                          {!isMobile && image}

                          {isMobile && !isEmpty(item.itemMaterial) && <div className={cx('text-base font-bold flex flex-row items-center h-fit')}>
                              <span className="mr-2">Purchased in: </span>
                              <PDPActiveFabric isEditOrder={false} isModularBundleProduct={false} isSelectedOptionsQuickship={false} loading={false} onClick={() => {
                      setSelectedMaterialId(item.itemMaterial.id);
                      setGalleryType('FabricMultiImg');
                      setSidebarOpen(true);
                    }} selectedOption={item.itemMaterial} size="xs" showInfoIcon={false} materialNameClass="text-base text-brand font-bold" fullHeight className="!mb-0" />
                            </div>}

                          <div className="text-base mb-2.5">
                            <SanitizedHtml as="span" html={item.comment} />
                          </div>

                          {!isEmpty(item.itemMaterial) && !isMobile ? <button className="flex flex-row w-full" ref={element => {
                    containerRefs.current[`${item.id}-${item.itemMaterial.id}`] = element;
                  }} type="button" onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    history.push(item.materialLink.replace('customer-reviews', ''));
                  }}>
                              <div className="h-[59px] min-w-[59px] w-[59px] max-[1023px]:flex-shrink-0">
                                <MaterialThumbnail image={item.itemMaterial.image} upholsteryOptionId={upholsteryOptionId} materialId={item.itemMaterial.id} label={item.itemMaterial.value} toggleFlyOut={customToggleFlyout} customToggleFlyOut customToggleFlyOutData={item} variant="PDPCustomerPhotosReviewsModal" />
                              </div>

                              <div className="text-base font-bold flex flex-row max-[1023px]:h-[59px] items-center ml-4">
                                <span className="mr-2">Material: </span>

                                {!item.itemMaterialInactive ? <Link replace to={item.materialLink.replace('customer-reviews', '')} className="text-base text-brand font-bold">
                                    <span>{item.itemMaterial.value}</span>
                                  </Link> : <BrandTooltip openOnHover text={item.itemMaterial.value} textColor="brand" showCloseButton={isMobile} direction="top" align="standard" showInfoIcon={false}>
                                    {`The ${name} is no longer available in ${item.itemMaterial.value}.`}
                                  </BrandTooltip>}
                              </div>
                            </button> : null}
                        </div>}
                      <div className="text-base font-bold px-5 py-0 md:w-[22%] md:pl-0 md:pr-[1%]">
                        <div className="max-[767px]:flex">
                          {!!item.full_name && <>
                              {!isMobile && <div className="w-11 h-11 max-h-[3.75rem] mb-4 text-2xl text-gray-light6 bg-gray-light8 rounded-full mr-4 flex justify-center items-center">
                                  {item.full_name?.[0]}
                                </div>}

                              <div className="PDPCustomerPhotosReviewsModal__user-details">
                                <div className="[&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c] flex items-center justify-start">
                                  <span className="text-base font-bold">
                                    {getShortCustomerName(item.full_name)}
                                  </span>
                                  <InfoTooltip withArrow modifier="brand-fade" customIcon={<IconVerified />} className="[&_.inline_svg]:w-[14px] [&_.inline_svg]:h-[14px] [&_.inline_svg]:ml-2 [&_.inline_svg]:mr-0" variant="PDPCustomerReviews">
                                    <div className="text-xs m-0 normal-case">
                                      Verified purchase
                                    </div>
                                  </InfoTooltip>
                                </div>
                                <div className="text-xs">{item.location}</div>
                              </div>
                            </>}
                        </div>
                      </div>
                    </div>;
            })}
                {allReviews?.length !== filteredReviews.length && <div className="w-full text-center">
                    <CtaButton
              // If we're detecting long presses, the onClick handler will be
              // handled by the long press hook so should not be triggered here
              onClick={() => setNoOFReviewToShow(prevNumber => {
                if (prevNumber !== allReviews.length) return prevNumber + 50;
                return prevNumber;
              })} styling="outline-button">
                      Load More
                    </CtaButton>
                  </div>}
              </div>
            </>}
        </div>
      </div>
    </>;
};
PDPCustomerPhotosReviewsModal.propTypes = {
  name: PropTypes.string,
  loading: PropTypes.bool,
  customerPhotos: PropTypes.bool,
  averageRating: PropTypes.number,
  totalReviews: PropTypes.number,
  initialImageIndex: PropTypes.number,
  initialReviewIndex: PropTypes.number,
  upholsteryOptionId: PropTypes.string,
  customerReviews: PropTypes.shape({
    length: PropTypes.number,
    reviews: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      comment: PropTypes.string,
      date: PropTypes.string,
      full_name: PropTypes.string,
      location: PropTypes.string,
      rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })),
    totalReviews: PropTypes.number,
    averageRating: PropTypes.number
  }),
  selectedMaterial: PropTypes.object
};
export default PDPCustomerPhotosReviewsModal;